/**
 * SEO component for Gatsby Head API
 * 
 * See: https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-head/
 */

import * as React from "react";
import PropTypes from "prop-types";
import { useStaticQuery, graphql } from "gatsby";
import { metaText } from "../utils/prismicMeta";

// Property documents have no uid, so a landing page is matched by its link.
// Those urls are inconsistent in the live repository — "/property/nova" is
// relative, motto's is http://, olea's has a trailing slash — so compare only
// the final path segment. Mirrors the matcher in @components/AlertBar.
const matchesSlug = (propertyNode, propertySlug) => {
  const url = propertyNode?.data?.property_link?.url;
  if (!url || !propertySlug) return false;
  return url.replace(/\/$/, '').endsWith(`/${propertySlug}`);
};

export function Head({ description, lang = `en`, meta = [], title, icon = '', ogImage = '', ogUrl = '',
  prismicMeta = null, propertySlug = '', homepage = false }) {
  const data = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
          }
        }
        allPrismicProperty {
          nodes {
            data {
              property_link {
                url
              }
              meta_title {
                text
              }
              meta_description {
                text
              }
            }
          }
        }
        prismicGomesHomepageGlobal {
          data {
            meta_title {
              text
            }
            meta_description {
              text
            }
          }
        }
      }
    `
  );
  const site = data.site;

  // Optional Prismic override, from the "Meta Info" tab. A landing page can be
  // authored on either its Property document or the unit document it happens
  // to be built from, because only Envy and Arbol have a page component of
  // their own — the other six properties render their landing page from a
  // single unit. Reaching for the Property document is the obvious move, so
  // it wins; the unit document still works so that neither choice silently
  // does nothing. Sofía has no Property document and resolves via its unit.
  // Blank fields fall through to whatever the page hardcodes.
  const propertyDoc = propertySlug
    ? data.allPrismicProperty?.nodes?.find(node =>
        matchesSlug(node, propertySlug)
      )
    : null;
  const globalDoc = homepage ? data.prismicGomesHomepageGlobal : null;

  // Resolved per field rather than per document, so a Property document with
  // only a title filled in lets the unit document still supply the
  // description instead of blanking it.
  const pick = field => {
    const sources = [propertyDoc?.data, globalDoc?.data, prismicMeta];
    for (const source of sources) {
      const text = metaText(source?.[field]);
      if (text) return text;
    }
    return null;
  };

  const overrideTitle = pick('meta_title');
  const overrideDescription = pick('meta_description');

  const resolvedTitle = overrideTitle || title;
  const metaDescription = overrideDescription || description || site.siteMetadata.description;

  // Build base meta tags
  const baseMetaTags = [
    {
      name: `description`,
      content: metaDescription,
    },
    {
      property: `og:title`,
      content: resolvedTitle,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      name: "google-site-verification",
      content: "BkCG_275O1Ti3eiBOmEU0jWTGW1v0g0zZfG17Gy2mvI"
    }
  ];

  if (ogImage) {
    baseMetaTags.push({
      property: `og:image`,
      content: ogImage,
    });
  }

  if (ogUrl) {
    baseMetaTags.push({
      property: `og:url`,
      content: ogUrl,
    });
  }

  baseMetaTags.push(
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:creator`,
      content: site.siteMetadata?.author || ``,
    },
    {
      name: `twitter:title`,
      content: resolvedTitle,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    }
  );

  // Several pages pass a `meta` array that deliberately gives og:title /
  // og:description different copy than <title>. A Prismic-authored value has
  // to beat that too, so it is appended last (the dedupe below is last-wins).
  // When nothing is authored this array is empty and the output is unchanged.
  const overrideTags = [];
  if (overrideTitle) {
    overrideTags.push(
      { property: `og:title`, content: overrideTitle },
      { name: `twitter:title`, content: overrideTitle }
    );
  }
  if (overrideDescription) {
    overrideTags.push(
      { name: `description`, content: overrideDescription },
      { property: `og:description`, content: overrideDescription },
      { name: `twitter:description`, content: overrideDescription }
    );
  }

  // Merge with additional meta tags, allowing overrides
  // If meta prop contains duplicates, they will override base tags (last one wins)
  const allMetaTags = [...baseMetaTags, ...(meta || []), ...overrideTags];
  
  // Deduplicate meta tags - keep the last occurrence of each property/name combination
  const metaMap = new Map();
  allMetaTags.forEach(tag => {
    const key = tag.property ? `property:${tag.property}` : `name:${tag.name}`;
    metaMap.set(key, tag);
  });
  const deduplicatedMeta = Array.from(metaMap.values());

  const linkTags = [];
  if (icon) {
    linkTags.push({ rel: 'shortcut icon', type: 'image/png', href: icon });
  }

  return (
    <>
      <html lang={lang} />
      <title>{resolvedTitle}</title>
      {deduplicatedMeta.map((tag, index) => {
        if (tag.property) {
          return <meta key={index} property={tag.property} content={tag.content} />;
        }
        return <meta key={index} name={tag.name} content={tag.content} />;
      })}
      {linkTags.map((link, index) => (
        <link key={index} {...link} />
      ))}
    </>
  );
}

Head.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  ogImage: PropTypes.string,
  ogUrl: PropTypes.string,
  // The Meta Info fields as queried from Prismic; each is a StructuredText
  // value, so `{ text }` in practice, and null before an editor fills it in.
  prismicMeta: PropTypes.shape({
    meta_title: PropTypes.object,
    meta_description: PropTypes.object,
  }),
  propertySlug: PropTypes.string,
  homepage: PropTypes.bool
};

// Default export for backward compatibility during migration
export default Head;
